import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  Print as PrintIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { payrollApi } from '../../../api/payrollApi';
import { PayslipDetailDto, PayslipDto } from '../../../types/payrollTypes';
import PayslipTemplateRenderer, { buildPayslipPrintHtml } from '../components/PayslipTemplateRenderer';

const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const MyPayslipsPage: React.FC = () => {
  const [payslips, setPayslips] = useState<PayslipDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<PayslipDetailDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        setPayslips(await payrollApi.getMyPayslips());
      } catch {
        toast.error('Failed to load payslips');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const handleView = async (id: string) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      setDetail(await payrollApi.getMyPayslipDetail(id));
    } catch {
      toast.error('Failed to load payslip detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(buildPayslipPrintHtml(printRef.current.innerHTML));
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>My Payslips</Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Month/Year</TableCell>
                  <TableCell>Generated At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payslips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">No payslips found</TableCell>
                  </TableRow>
                ) : (
                  payslips.map((slip) => (
                    <TableRow key={slip.id}>
                      <TableCell>{`${monthNames[slip.month] || slip.month} ${slip.year}`}</TableCell>
                      <TableCell>{new Date(slip.generatedAt).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Payslip">
                          <IconButton size="small" color="primary" onClick={() => handleView(slip.id)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Box ref={printRef}>
              <PayslipTemplateRenderer detail={detail} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {detail && <Button onClick={handlePrint} startIcon={<PrintIcon />} variant="outlined">Print</Button>}
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPayslipsPage;
